import React, { useState } from 'react';

const typeConfig = {
  success: { color: 'var(--success)', bg: 'var(--success-bg)', border: 'var(--success-border)' },
  error:   { color: 'var(--error)',   bg: 'var(--error-bg)',   border: 'var(--error-border)' },
  warning: { color: 'var(--warning)', bg: 'var(--warning-bg)', border: 'var(--warning-border)' },
};

export default function StatusCodes({ statusCodes = [] }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? statusCodes : statusCodes.filter(s => s.type === filter);
  const counts = {
    all: statusCodes.length,
    success: statusCodes.filter(s => s.type === 'success').length,
    error: statusCodes.filter(s => s.type === 'error').length,
    warning: statusCodes.filter(s => s.type === 'warning').length,
  };

  return (
    <div className="page-content">

      {/* Header */}
      <div style={{ marginBottom: 36 }}>
        <h1 className="page-title">
          Status Codes
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Every API response includes a <code style={{ color: 'var(--accent-light)', background: 'var(--accent-bg)', padding: '2px 8px', borderRadius: 5, fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>statusCode</code> field.
          Use these to handle errors gracefully in your integration.
        </p>
      </div>

      {/* Response Structure */}
      <div className="grid-2" style={{ marginBottom: 36 }}>
        {[
          { label: '✅ Success Response', code: `{\n  "status": true,\n  "statusCode": 200,\n  "data": { ... }\n}`, type: 'success' },
          { label: '❌ Error Response',   code: `{\n  "status": false,\n  "statusCode": 401,\n  "message": "Request failed"\n}`, type: 'error' },
        ].map((item, i) => {
          const c = typeConfig[item.type];
          return (
            <div key={i} style={{ background: 'var(--bg-surface)', border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden', minWidth: 0 }}>
              <div style={{ padding: '8px 14px', background: c.bg, borderBottom: `1px solid ${c.border}`, fontSize: 12, fontWeight: 600, color: c.color }}>
                {item.label}
              </div>
              <pre style={{ margin: 0, padding: '14px 16px', fontSize: 12, lineHeight: 1.7, color: c.color, fontFamily: 'JetBrains Mono, monospace', background: 'transparent', overflowX: 'auto' }}>
                {item.code}
              </pre>
            </div>
          );
        })}
      </div>

      {/* Filter Tabs */}
      <div className="status-filter">
        {['all', 'success', 'error', 'warning'].map(f => {
          const isActive = filter === f;
          const c = f !== 'all' ? typeConfig[f] : null;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 16px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 500, fontFamily: 'Inter, sans-serif',
                background: isActive ? (c ? c.bg : 'var(--bg-base)') : 'transparent',
                color: isActive ? (c ? c.color : 'var(--text-primary)') : 'var(--text-muted)',
                boxShadow: isActive && !c ? 'var(--shadow-sm)' : 'none',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span style={{
                fontSize: 10, fontWeight: 700, minWidth: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 20, background: isActive ? (c ? c.color + '20' : 'var(--bg-elevated)') : 'var(--bg-elevated)',
                color: isActive ? (c ? c.color : 'var(--text-secondary)') : 'var(--text-muted)',
                padding: '0 5px',
              }}>{counts[f]}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="status-table-wrap">
        <div className="status-table-head">
          {['Code', 'Message', 'Description'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</div>
          ))}
        </div>

        {filtered.map((sc, i) => {
          const c = typeConfig[sc.type] || typeConfig.error;
          return (
            <div
              key={i}
              className="status-table-row"
              style={{
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                background: 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div className="status-cell-code" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11.5, fontWeight: 800,
                padding: '3px 10px', borderRadius: 8,
                background: c.bg, color: c.color, border: `1px solid ${c.border}`,
                fontFamily: 'JetBrains Mono, monospace',
                width: 'fit-content',
              }}>{sc.code}</div>
              <div className="status-cell-msg" style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.5 }}>{sc.message}</div>
              <div className="status-cell-desc" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{sc.description}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
